/* eslint-disable no-script-url */
import * as React from "react";
import {
  Container,
  Divider,
  Grid,
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  TextField,
} from "@mui/material";
import { VENDOR_PROFILE_LISTING } from "../../utils/vendorProfileConstant";
import { useEffect } from "react";
import {
  editVendorDetailsService,
  getVendorDetailsService,
} from "../../services/vendorProfileServices";
import useGlobalState from "../../Context";
import { setVendorDetails } from "../../reducer/action";
import { useNotificationContext } from "../../common/NotificationContext";
import { useLoading } from "../../common/LoadingContext";
import { Cancel, Edit, Save } from "@material-ui/icons";
import { DetailSection } from "../../common/DetailSection";
import { deepEqual, padPO } from "../../utils/jsUtils";
import { VENDOR_PROFILE } from "../../utils/Validation";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

const ProfileDetail = () => {
  const { setLoading } = useLoading();
  const { showNotification } = useNotificationContext();
  const [{ userDetails, vendorDetails }, dispatch] = useGlobalState();
  const [localVendorDetails, setLocalVendorDetails] = React.useState(null);
  const [editMode, setEditMode] = React.useState(false);
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const userParam = userDetails?.userParam;
  const userName = userParam?.userName;
  const formOption = {
    resolver: yupResolver(VENDOR_PROFILE),
  };
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm(formOption);

  const onSubmit = async (data) => {
    try {
      if (!deepEqual(data, vendorDetails)) {
        setLoading(true);
        const editVendorDetailsResponse = await editVendorDetailsService(
          baseUrl,
          {
            ...data,
            userName: userName,
          }
        );
        if (editVendorDetailsResponse?.messageCode === 6) {
          dispatch(setVendorDetails({ ...data }));
          setEditMode(false);
        } else {
          showNotification(
            "Error",
            editVendorDetailsResponse?.message ??
              "Failed To Edit Vendor Details"
          );
        }
      } else {
        setEditMode(false);
      }
    } catch (err) {
      showNotification(
        "Error",
        err?.message ?? "Failed To Edit Vendor Details"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorDetails = async () => {
    try {
      if (localVendorDetails === null) {
        setLoading(true);

        const vendorCode = userParam?.vendorCode;

        const requestObj = {
          vendorCode: padPO(vendorCode),
        };
        const vendorDetailsResponse = await getVendorDetailsService(
          baseUrl,
          requestObj
        );
        if (vendorDetailsResponse.messageCode === 6) {
          dispatch(setVendorDetails(vendorDetailsResponse?.vendorDetailsParam));
          setLocalVendorDetails(vendorDetailsResponse?.vendorDetailsParam);
          setValue(
            "applicationId",
            vendorDetailsResponse?.vendorDetailsParam?.applicationId
          );
          setValue(
            "fcLocation",
            vendorDetailsResponse?.vendorDetailsParam?.fcLocation
          );
        } else {
          showNotification(
            "Error",
            vendorDetailsResponse?.message ?? "Failed to Fetch Vendor Details"
          );
        }
      }
    } catch (err) {
      showNotification(
        "Error",
        err?.message ?? "Failed to Fetch Vendor Details"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendorDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Container maxWidth="xl" sx={{ mt: 8, px: 1 }} disableGutters>
        <Box display={"flex"} justifyContent="center" mx={3}>
          <Paper elevation={3} variant="elevation" sx={{ width: "100%" }}>
            <Grid sx={{ background: "#fff" }}>
              <Grid
                container
                display={"flex"}
                justifyContent="space-between"
                mb={2}
                mt={1}
              >
                <Grid ml={1} item xs={4} md={4}>
                  <Typography
                    color="primary"
                    sx={{ textAlign: "left", pl: 4, pt: 4 }}
                    variant="h6"
                    display="block"
                    gutterBottom
                  >
                    {"Vendor Details"}
                  </Typography>
                </Grid>

                <Grid item xs={4} md={4} alignSelf="end" mr={2}>
                  {!editMode && (
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => {
                        setEditMode(true);
                      }}
                    >
                      Edit Profile
                    </Button>
                  )}
                </Grid>
              </Grid>
              <Divider />
              {!editMode ? (
                <Grid container sx={{ px: 3, pt: 2, pb: 1 }}>
                  {VENDOR_PROFILE_LISTING.map((data, index) => {
                    const heading = data.label;

                    const value = vendorDetails?.[data.id];

                    return (
                      <Grid
                        item
                        xs={data.id === "address" ? 12 : 6}
                        md={data.id === "address" ? 12 : 4}
                        textAlign={"left"}
                        key={`${index}-grid-key`}
                      >
                        <DetailSection
                          heading={heading}
                          content={value}
                          index={index}
                          key={`${index}-details-key`}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Grid container sx={{ px: 3, pt: 2, pb: 1 }}>
                  <Grid
                    item
                    xs={6}
                    md={4}
                    textAlign={"left"}
                    key={`vendorName-grid-key`}
                  >
                    <Box sx={{ mt: 1, mb: 3, px: 3 }} key={"vendorName"}>
                      <Typography
                        component={"div"}
                        variant="body1"
                        // style={{ color: "#9e9e9e" }}
                        fontSize={13}
                        key={"vendorName"}
                      >
                        Vendor Name
                      </Typography>

                      <FormControl fullWidth>
                        <TextField
                          disabled={true}
                          id={"vendorName"}
                          value={localVendorDetails?.["vendorName"]}
                          name={"vendorName"}
                          className="input-correction"
                          {...register("vendorName", {
                            value: localVendorDetails?.["vendorName"],
                          })}
                          size="small"
                        ></TextField>
                      </FormControl>
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={6}
                    md={4}
                    textAlign={"left"}
                    key={`vendorCode-grid-key`}
                  >
                    <Box sx={{ mt: 1, mb: 3, px: 3 }} key={"vendorName"}>
                      <Typography
                        component={"div"}
                        variant="body1"
                        // style={{ color: "#9e9e9e" }}
                        fontSize={13}
                        key={"vendorName"}
                      >
                        Vendor Code
                      </Typography>

                      <FormControl fullWidth>
                        <TextField
                          disabled={true}
                          id={"vendorCode"}
                          {...register("vendorCode", {
                            value: localVendorDetails?.["vendorCode"],
                          })}
                          value={localVendorDetails?.["vendorCode"]}
                          name={"vendorCode"}
                          className="input-correction"
                          size="small"
                        ></TextField>
                      </FormControl>
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={6}
                    md={4}
                    textAlign={"left"}
                    key={`email-grid-key`}
                  >
                    <Box sx={{ mt: 1, mb: 3, px: 3 }} key={"email"}>
                      <Typography
                        component={"div"}
                        variant="body1"
                        // style={{ color: "#9e9e9e" }}
                        fontSize={13}
                        key={"email"}
                      >
                        Email
                      </Typography>

                      <FormControl fullWidth>
                        <TextField
                          id={"email"}
                          type="email"
                          defaultValue={localVendorDetails?.["email"]}
                          name={"email"}
                          {...register("email")}
                          className="input-correction"
                          size="small"
                          error={
                            !!errors?.email
                              ? Boolean(`${errors?.email}`)
                              : false
                          }
                          helperText={
                            !!errors?.email ? `${errors?.email?.message}` : ""
                          }
                        ></TextField>
                      </FormControl>
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={6}
                    md={4}
                    textAlign={"left"}
                    key={`mobile-grid-key`}
                  >
                    <Box sx={{ mt: 1, mb: 3, px: 3 }} key={"mobile"}>
                      <Typography
                        component={"div"}
                        variant="body1"
                        // style={{ color: "#9e9e9e" }}
                        fontSize={13}
                        key={"mobile"}
                      >
                        Vendor SPOC Mobile Number
                      </Typography>

                      <FormControl fullWidth>
                        <TextField
                          id={"mobile"}
                          defaultValue={localVendorDetails?.["mobile"]}
                          name={"mobile"}
                          {...register("mobile")}
                          className="input-correction"
                          inputProps={{ maxLength: 10 }}
                          size="small"
                          error={
                            !!errors?.mobile
                              ? Boolean(`${errors?.mobile}`)
                              : false
                          }
                          helperText={
                            !!errors?.mobile ? `${errors?.mobile?.message}` : ""
                          }
                        ></TextField>
                      </FormControl>
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={6}
                    md={4}
                    textAlign={"left"}
                    key={`firstName-grid-key`}
                  >
                    <Box sx={{ mt: 1, mb: 3, px: 3 }} key={"firstName"}>
                      <Typography
                        component={"div"}
                        variant="body1"
                        // style={{ color: "#9e9e9e" }}
                        fontSize={13}
                        key={"firstName"}
                      >
                        SPOC First Name
                      </Typography>

                      <FormControl fullWidth>
                        <TextField
                          defaultValue={localVendorDetails?.["firstName"]}
                          name={"firstName"}
                          {...register("firstName")}
                          className="input-correction"
                          required={true}
                          size="small"
                          error={
                            !!errors?.firstName
                              ? Boolean(`${errors?.firstName}`)
                              : false
                          }
                          helperText={
                            !!errors?.firstName
                              ? `${errors?.firstName?.message}`
                              : ""
                          }
                        ></TextField>
                      </FormControl>
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={6}
                    md={4}
                    textAlign={"left"}
                    key={`lastName-grid-key`}
                  >
                    <Box sx={{ mt: 1, mb: 3, px: 3 }} key={"lastName"}>
                      <Typography
                        component={"div"}
                        variant="body1"
                        // style={{ color: "#9e9e9e" }}
                        fontSize={13}
                        key={"lastName"}
                      >
                        SPOC Last Name
                      </Typography>

                      <FormControl fullWidth>
                        <TextField
                          id={"lastName"}
                          defaultValue={localVendorDetails?.["lastName"]}
                          {...register("lastName")}
                          name={"lastName"}
                          className="input-correction"
                          size="small"
                          error={
                            !!errors?.lastName
                              ? Boolean(`${errors?.lastName}`)
                              : false
                          }
                          helperText={
                            !!errors?.lastName
                              ? `${errors?.lastName?.message}`
                              : ""
                          }
                        ></TextField>
                      </FormControl>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {editMode && (
                <Grid
                  container
                  display="flex"
                  justifyContent={"end"}
                  px={2}
                  pb={2}
                >
                  <Grid item>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={() => {
                        setEditMode(false);
                      }}
                      color="error"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      sx={{ mx: 1 }}
                      color="success"
                      startIcon={<Save />}
                      onClick={handleSubmit(onSubmit)}
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default ProfileDetail;
